import { Maybe } from 'graphql/jsutils/Maybe';
import React, { useRef } from 'react';
import { useTheme } from 'styled-components';
import { Nova } from 'icons';
import {
  ChatbotInput,
  type ChatbotMessageSentSource,
  type UserMessage,
} from 'Assistant/Chatbot';
import useViewportWidth from 'utils/useViewportWidth';
import { Breakpoints } from '@unmind/design-system/theme/deprecated';
import { useSubmitInitialMessage } from 'Assistant/Chatbot/hooks';
import { isMSTeams } from 'utils/MSTeams';
import { useHistory } from 'react-router';
import RoutePath from 'App/RoutePath';
import { useTranslation } from 'react-i18next';
import useAssignFocus from '../../../Shared/Accessibility/hooks/useAssignFocus';
import { ChatbotStarters } from './ChatbotStarters';
import './ChatbotBanner.scss';

type ChatbotBannerProps = {
  firstName: Maybe<string>;
};

const ChatbotBanner = ({ firstName }: ChatbotBannerProps) => {
  const theme = useTheme();
  const viewportWidth = useViewportWidth();
  const { t: translate } = useTranslation('homepage');
  const history = useHistory();
  const isMobileWeb = viewportWidth < Breakpoints.XS;
  const [shouldSubmitInitialMessage, setSubmitInitialMessage] =
    useSubmitInitialMessage();
  const titleRef = useRef<HTMLHeadingElement>(null);
  useAssignFocus(titleRef);

  const handleSubmitRedirect = async (
    message: UserMessage,
    source: ChatbotMessageSentSource,
  ) => {
    if (!shouldSubmitInitialMessage) {
      await setSubmitInitialMessage(true);
    }

    if (isMSTeams()) {
      history.push(
        `${RoutePath.Chatbot}?content=${message.content}&topic=${message.topic}&starterId=${message.starterId}&source=${source}`,
      );
    } else {
      history.push(RoutePath.Chatbot, {
        ...message,
        source,
      });
    }
  };

  const greeting =
    (isMobileWeb && !isMSTeams()) || !firstName?.trim()
      ? translate('chatbot_banner.greeting_generic')
      : translate('chatbot_banner.greeting_personal', { name: firstName });

  return (
    <div className="chatbot-banner">
      <div
        className="chatbot-banner-title-container"
        data-testid="chatbot-banner-title-container"
      >
        <Nova
          aria-hidden={true}
          className="assistant-icon"
          primaryColor={theme.colors.chatbot.logo.primary}
          secondaryColor={theme.colors.chatbot.logo.secondary}
          tertiaryColor={theme.colors.chatbot.logo.tertiary}
        />
        <h1 ref={titleRef} tabIndex={-1} className="title-xl">
          {greeting}
        </h1>
      </div>
      <div className="chatbot-input-container">
        <ChatbotInput
          canSubmit={true}
          onSubmit={async message =>
            handleSubmitRedirect(message, 'home-chat-input')
          }
          displayOnHomepage={true}
        />
      </div>
      <ChatbotStarters
        handleSubmitRedirect={async message =>
          handleSubmitRedirect(message, 'home-conversation-starter')
        }
      />
    </div>
  );
};

export default ChatbotBanner;
